import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import AnimationCircle from './AnimationCircle';
import {
  formatDateTimeInResourceTz,
  formatGateClosureInResourceTz,
  getDayInResourceTz,
  getHourMinuteInResourceTz,
  getResourceTimezoneLabel,
  getResourceTimezoneLabels,
} from '../utils/datetime';
import {
  clearSubmissionHeatmap,
  fetchSubmissionHeatmap,
  selectSubmissionHeatmapData,
  selectSubmissionHeatmapLoading,
} from '../slices/reportsSlice';
import { isSessionOpen } from '../utils/submissionCalendar';
import { SUBMISSION_STATE } from '../utils/statusTokens';

export default function SubmissionHeatmap({
  resourceId,
  year,
  month,
  selectedHorizon,
  userId,
}) {
  const dispatch = useDispatch();
  const [activeSession, setActiveSession] = useState(null);
  const tooltipRef = useRef(null);
  const containerRef = useRef(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target) &&
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setActiveSession(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close tooltip when Escape is pressed
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setActiveSession(null);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  const handleCellClick = useCallback((sessionId) => {
    setActiveSession((prev) => (prev === sessionId ? null : sessionId));
  }, []);

  const heatmapData = useSelector(selectSubmissionHeatmapData);
  const heatmapLoading = useSelector(selectSubmissionHeatmapLoading);

  useEffect(() => {
    if (!resourceId || !year || !month) return;

    // selectedHorizon is optional — null/undefined means "all horizons"
    // (the backend skips the horizon_offset filter). Used by the compliance
    // empty-state to show every session for users whose horizon distribution
    // can't be determined ahead of time.
    dispatch(
      fetchSubmissionHeatmap({
        resourceId,
        year,
        month,
        horizonOffset: selectedHorizon,
        userId,
      })
    );

    return () => {
      dispatch(clearSubmissionHeatmap());
    };
  }, [dispatch, resourceId, year, month, selectedHorizon, userId]);

  const sessions = useMemo(
    () => (Array.isArray(heatmapData?.sessions) ? heatmapData.sessions : []),
    [heatmapData]
  );

  // Group sessions by (day, gate-closure hour) so every hour has its own row.
  // Indexing by hour instead of position guarantees labels stay aligned with
  // squares even when different days start/end at slightly different times.
  const { dayColumns, rowLabels, daysInMonth, tzLabels } = useMemo(() => {
    const daysInMonth = moment({ year, month: month - 1 }).daysInMonth();
    const columns = {};
    const hourSet = new Set();

    for (const session of sessions) {
      if (!session.gate_closure) continue;
      const day = getDayInResourceTz(session.gate_closure);
      const hour = getHourMinuteInResourceTz(session.gate_closure);
      if (day == null || !hour) continue;

      hourSet.add(hour);
      if (!columns[day]) columns[day] = {};
      columns[day][hour] = session;
    }

    const rowLabels = Array.from(hourSet).sort();
    // Rows are Brussels wall-clock hours for the whole month; March/October
    // months need both CET and CEST.
    const tzLabels = getResourceTimezoneLabels(sessions.map((s) => s.gate_closure));

    return { dayColumns: columns, rowLabels, daysInMonth, tzLabels };
  }, [sessions, year, month]);

  const maxSessions = rowLabels.length;

  const submittedCount = sessions.filter((s) => s.submitted).length;

  if (heatmapLoading) {
    return (
      <div>
        <div className="flex items-center justify-center py-2">
          <AnimationCircle size={5} />
        </div>
      </div>
    );
  }

  if (!sessions.length) {
    return (
      <div className="py-6 text-center text-xs text-gray-400 dark:text-gray-500">
        No data for the selected period.
      </div>
    );
  }

  // Build array of day numbers (1..daysInMonth)
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        {/* Zone of the hour rows (Brussels wall clock); both when the month
            straddles a DST switch. Same caption style as the Analytics heatmap. */}
        <span
          data-testid="heatmap-tz"
          className="text-[10px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400"
        >
          Gate times · {tzLabels.join('/')}
        </span>
        <span className="text-[10px] tabular-nums text-gray-400 dark:text-gray-500">
          {submittedCount} / {sessions.length} submitted
        </span>
      </div>

      <div ref={containerRef} className="overflow-visible">
        {/* Single grid: label column + day columns, with header row + data rows */}
        <div
          className="grid"
          style={{
            gridTemplateColumns: `30px repeat(${daysInMonth}, minmax(0, 1fr))`,
            gridTemplateRows: `auto repeat(${maxSessions}, 13px)`,
            gap: '1px',
          }}
        >
          {/* Top-left corner (empty cell above labels) */}
          <div />
          {/* Day number headers */}
          {days.map((day) => (
            <div
              key={`hdr-${day}`}
              className={`text-[8px] text-center font-mono tabular-nums text-gray-400 dark:text-gray-500 leading-tight pb-[2px] ${day === 1 || day % 5 === 0 ? '' : 'invisible xl:visible'}`}
            >
              {day}
            </div>
          ))}

          {/* Data rows: label + cells */}
          {rowLabels.map((hour) => (
            <React.Fragment key={`row-${hour}`}>
              {/* Y-axis label */}
              <div
                className="text-[9px] font-mono tabular-nums text-gray-400 dark:text-gray-500 flex items-center justify-end pr-1 overflow-hidden"
                style={{ height: '13px', lineHeight: 1 }}
              >
                {hour}
              </div>
              {days.map((day) => {
                const session = dayColumns[day]?.[hour];

                if (!session) {
                  return (
                    <div
                      key={`empty-${day}-${hour}`}
                      style={{ height: '13px' }}
                    />
                  );
                }

                const closure = formatGateClosureInResourceTz(session.gate_closure);
                const gateClosure = closure
                  ? `${closure.date} · ${closure.time} ${getResourceTimezoneLabel(undefined, new Date(session.gate_closure))}`
                  : 'Unknown';
                const targetDay = session.target_day
                  ? moment(session.target_day).format('MMM DD, YYYY')
                  : 'Unknown';
                // A session is still open for submission until its gate
                // closure passes. We deliberately key off gate_closure (not the
                // backend `status` field, which lingers at 'open' until the
                // market actually runs) so this matches the dashboard hero's
                // open/locked verdict. Only a non-submitted session whose gate
                // has already passed counts as genuinely "Missed". Shared with
                // SubmissionCalendar (isSessionOpen) so the two never disagree.
                const isOpen = isSessionOpen(session);
                const statusLabel = session.submitted
                  ? 'Submitted'
                  : isOpen
                    ? 'Open'
                    : 'Missed';
                const isActive = activeSession === session.session_id;

                // Position tooltip to avoid clipping at edges
                const isLeftEdge = day <= 3;
                const isRightEdge = day >= daysInMonth - 2;
                const tooltipAlign = isLeftEdge
                  ? 'left-0'
                  : isRightEdge
                    ? 'right-0'
                    : 'left-1/2 -translate-x-1/2';
                const arrowAlign = isLeftEdge
                  ? 'left-1.5'
                  : isRightEdge
                    ? 'right-1.5'
                    : 'left-1/2 -translate-x-1/2';

                return (
                  <div
                    key={session.session_id}
                    data-testid={`gate-closure-group-${formatDateTimeInResourceTz(session.gate_closure).slice(0, 10)}`}
                    className="relative group"
                    style={{ height: '13px' }}
                  >
                    <button
                      type="button"
                      aria-label={`Session #${session.session_id}: ${statusLabel}, gate ${gateClosure}`}
                      onClick={() => handleCellClick(session.session_id)}
                      style={{ height: '13px', padding: 0, lineHeight: 0 }}
                      className={`w-full block rounded-[1px] transition-colors cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-500 ${
                        session.submitted
                          ? SUBMISSION_STATE.ready.cell
                          : isOpen
                            ? SUBMISSION_STATE.pending.cell
                            : SUBMISSION_STATE.missed.cell
                      } ${isActive ? 'ring-1 ring-brand-500' : ''}`}
                    />
                    {/* Tooltip */}
                    <div
                      ref={isActive ? tooltipRef : null}
                      className={`
                        absolute bottom-full ${tooltipAlign} mb-1.5 z-50
                        pointer-events-none whitespace-nowrap
                        rounded-md bg-gray-900 dark:bg-gray-800 text-white
                        px-2.5 py-1.5 text-[10px] leading-relaxed shadow-lg
                        transition-opacity duration-100
                        ${isActive
                          ? 'opacity-100 pointer-events-auto'
                          : 'opacity-0 group-hover:opacity-100'
                        }
                      `}
                    >
                      <div className="font-semibold">Session #{session.session_id}</div>
                      <div className="text-gray-300 dark:text-gray-400">Gate: {gateClosure}</div>
                      <div className="text-gray-300 dark:text-gray-400">Target: {targetDay}</div>
                      <div className={session.submitted ? 'text-green-400' : isOpen ? 'text-amber-400' : 'text-red-400'}>
                        {statusLabel}
                      </div>
                      <div className={`absolute top-full ${arrowAlign} w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-900 dark:border-t-gray-700`} />
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="mt-2 flex items-center justify-end gap-1.5 text-[9px] text-gray-400 dark:text-gray-500">
        <span className="mr-auto text-gray-500 dark:text-gray-400">Select a cell for details</span>
        <span className={`h-2.5 w-2.5 rounded-[2px] ${SUBMISSION_STATE.missed.cell}`} />
        <span>Missed</span>
        <span className={`h-2.5 w-2.5 rounded-[2px] ${SUBMISSION_STATE.pending.cell}`} />
        <span>Open</span>
        <span className={`h-2.5 w-2.5 rounded-[2px] ${SUBMISSION_STATE.ready.cell}`} />
        <span>Submitted</span>
      </div>
    </div>
  );
}
